(ns app.front.view.complaint.public-view
  (:require ["@carbon/icons-react/es/close--filled/16" :default CloseFilled16]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :as futil]
            [app.front.view.complaint.add-form :as complaint-form]
            [app.front.view.create-application.create-v2 :as create-v2]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.landing-page :as landing-page]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ branding origin]]
    {:db           (assoc db ::data {::origin origin})
     :fx           [[:dispatch [::complaint-form/clear-data]]
                    [:dispatch [::gis-search/set-search-value ""]]]
     :comm/command {:command    :complaint/public-config
                    :method     :get
                    :data       {:branding branding}
                    :on-success [::init.success]}}))


(rf/reg-event-fx ::init.success
  (fn [{:keys [db]} [_ _ config]]
    {:db (assoc-in db [::data ::config] config)
     :fx [[:dispatch [::complaint-form/set-disclaimer-configs
                      (:permit-types config)]]
          [:dispatch [::load-map-layers (-> config :domain-group :group/id)]]]}))


(rf/reg-event-fx ::select-property
  (fn [{:keys [db]} [_ point feature]]
    {:db (-> db
             (assoc-in [::data ::selected-property] feature)
             (assoc-in [::data ::coordinates] point)
             (update ::data dissoc ::location))
     :fx [[:dispatch [::gis-search/set-search-value (or (-> feature :address :address/full-address) "")]]
          [:dispatch [::search-property feature]]]}))


(rf/reg-event-fx ::search-property
  (fn [{:keys [db]} [_ feature]]
    (let [config (get-in db [::data ::config])]
      (when (:public-records-enabled? config)
        {:comm/command {:command    :public-map/search-v2
                        :data       {:permit-types    (-> config
                                                          :permit-types
                                                          keys
                                                          set
                                                          (set/intersection ptc-model/permit-types-with-complaints))
                                     :domain-group-id (-> config :domain-group :group/id)
                                     :shape           (:geometry feature)}
                        :on-success [::search-property.success
                                     (-> feature :property :property/id)]}}))))


(rf/reg-event-db ::search-property.success
  (fn [db [_ property-id _ {:keys [features]}]]
    (assoc-in db [::data ::property-complaints property-id] features)))


(rf/reg-event-fx ::map-click
  (map-common-util/click-handler-fn {:search-success-evt   ::map-click.success
                                     :search-not-found-evt ::map-click.not-found
                                     :do-point-search?     true}))


(rf/reg-event-fx ::map-click.success
  (fn [_ [_ click-coordinates _ feature]]
    {:dispatch [::select-property click-coordinates feature]}))


(rf/reg-event-db ::map-click.not-found
  (fn [db [_ coordinates]]
    (-> (update db ::data dissoc ::selected-property)
        (assoc-in [::data ::coordinates] coordinates))))


(rf/reg-event-fx ::deselect-property
  (fn [{:keys [db]} _]
    {:db (-> db
             (update ::data dissoc ::selected-property ::coordinates ::location)
             (dissoc ::create-v2/manual-input ::create-v2/manual-address))
     :fx [[:dispatch [::gis-search/set-search-value ""]]]}))


(rf/reg-event-fx ::begin-complaint
  (fn [{:keys [db]} _]
    (let [selected-property (get-in db [::data ::selected-property])
          location          (when-not selected-property
                              (get-in db [::data ::location]))]
      {:db       (assoc-in db [::data ::step] :form)
       :dispatch [::complaint-form/start-create-complaint location]})))


(rf/reg-event-fx ::use-location
  (fn [{:keys [db]} [_ location]]
    {:db       (assoc-in db [::data ::location] location)
     :dispatch [::begin-complaint]}))


(rf/reg-event-fx ::back-to-location
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::data ::step] :location)
     :dispatch [::complaint-form/clear-data]}))


(defn previous-view [origin logged-in?]
  (cond
    (= origin "public-records")
    :view/public-records

    logged-in?
    :dashboard

    :else
    :view/landing-page))


(rf/reg-event-fx ::to-previous-view
  (fn [{:keys [db]} _]
    (let [origin     (get-in db [::data ::origin])
          logged-in? (:session/user db)]
      (when (::data db)
        {:db       (dissoc db ::data)
         :dispatch [:route/push (previous-view origin logged-in?)]}))))


(rf/reg-event-fx ::complaint-created
  (fn [{:keys [db]} _]
    {:db             (assoc-in db [::data ::step] :thank-you)
     :dispatch-later {:ms       4000
                      :dispatch [::to-previous-view]}}))


(rf/reg-event-fx ::leave
  (fn [{:keys [db]} _]
    {:db (dissoc db ::data)
     :fx [[:dispatch [::complaint-form/clear-data]]
          [:dispatch [::gis-search/set-search-value ""]]]}))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::config
  :<- [::data]
  :-> ::config)


(rf/reg-sub ::origin
  :<- [::data]
  :-> ::origin)


(rf/reg-sub ::loading?
  :<- [:comm/command-processing? :complaint/public-config]
  :-> identity)


(rf/reg-sub ::municipalities
  :<- [::config]
  (fn [config _]
    (-> config :municipalities)))


(rf/reg-sub ::group-id
  :<- [::config]
  (fn [config _]
    (-> config :domain-group :group/id)))


(rf/reg-event-fx ::load-map-layers
  (fn [_ [_ domain-group-id]]
    {:comm/command {:command    :complaint/public-map-layers
                    :method     :get
                    :data       {:domain-group-id domain-group-id}
                    :on-success [::load-map-layers.success]}}))

(rf/reg-event-db ::load-map-layers.success
  (fn [db [_ _ {:keys [map-layers]}]]
    (assoc-in db [::data ::gis-map-layers] map-layers)))

(rf/reg-sub ::gis-map-layers
  :<- [::data]
  :-> ::gis-map-layers)


(rf/reg-sub ::complaint-authority-groups
  :<- [::config]
  :<- [:language]
  (fn [[config lang] _]
    (let [complaint-permit-type-configs (-> (:permit-types config)
                                            (select-keys ptc-model/permit-types-with-complaints)
                                            vals)]
      (->> complaint-permit-type-configs
           (map (fn [ptc]
                  {:authority-group-id        (:authority-group-id ptc)
                   :permit-type               (:permit-type ptc)
                   :categories                (some->> (:categories ptc)
                                                       (filter #(config-model/allow-workspace-creation? false %))
                                                       (map #(assoc % :authority-group-id (:authority-group-id ptc)))
                                                       (config-model/sort-group-configs lang))
                   :required-complaint-fields (-> ptc :group.config/ce-complaint-required-contact-information set)
                   :group-features            (:group-features ptc)
                   :hide-severity?            (:hide-severity? ptc)
                   :complaint-severity-levels (:complaint-severity-levels ptc)
                   :on-success-evt            ::complaint-created}))))))


(rf/reg-sub ::selected-property
  :<- [::data]
  :-> ::selected-property)


(rf/reg-sub ::coordinates
  :<- [::data]
  :-> ::coordinates)


(rf/reg-sub ::map-center
  :<- [::coordinates]
  :<- [::municipalities]
  (fn [[coordinates municipalities] _]
    (or coordinates
        (->> municipalities
             (keep :municipality/center-location)
             municipality-model/center-of-centers))))


(rf/reg-sub ::step
  :<- [::data]
  (fn [data _]
    (or (::step data) :location)))


(rf/reg-sub ::location
  :<- [::data]
  :-> ::location)


(defn- category-names-with-frequencies [filter-fn content]
  (some->> content
           (filter filter-fn)
           seq
           (mapv :category-names)
           frequencies))


(rf/reg-sub ::property-complaints
  :<- [::data]
  :<- [::selected-property]
  (fn [[data selected-property] _]
    (let [{:entity.type/keys [complaint workspace]} (some->> selected-property
                                                             :property
                                                             :property/id
                                                             (get (data ::property-complaints))
                                                             (mapv :properties)
                                                             (group-by :entity/type))]
      (p/assoc-if {}
                  :complaints  (category-names-with-frequencies (partial du/in-state? :complaint.state/open) complaint)
                  :work-orders (category-names-with-frequencies #(= :permit-type/PWO (:workspace/permit-type %)) workspace)
                  :all-cases   (category-names-with-frequencies #(= :permit-type/CE (:workspace/permit-type %)) workspace)))))


(rf/reg-sub ::searching-property?
  :<- [:comm/command-processing? :public-map/search-v2]
  identity)


(defn- existing-content-block [tr lang content-key content]
  [:div.mb-4
   [:h4.text-sm.font-bold.uppercase.mb-2 (tr [(keyword "public-map-view" content-key)])]
   (futil/for-frag [c (keys content)]
     ^{:key c}
     [:div.p-3.bg-gray-200.mt-1.rounded.text-sm.font-bold.justify-between.flex.items-center
      [:div (i18n-model/get-text-from-entities-or-map c lang)]
      [:div (get content c)]])])


(defn- existing-feature-properties [{:keys [tr lang]}]
  (let [content    (<sub [::property-complaints])
        searching? (<sub [::searching-property?])]
    (when (or searching? (seq content))
      (if searching?
        (cc/inline-loading {:description (tr [:loading])})
        [:div
         (futil/for-frag [cat (keys content)]
           ^{:key cat}
           [existing-content-block tr lang cat (cat content)])]))))

(def map-info :div.flex-col.outline.outline-2.outline-gray-200.map-common-info-box.rounded.w-full.lg:w-1-4.pl-2.pr-2.py-2.lg:ml-2.flex.bg-ui-white-10.justify-between)

(defn- property-details [{:keys [tr lang selected-property coordinates point-search-loading?]}]
  (let [address (-> selected-property :address :address/full-address)]
    (cond
      point-search-loading?
      [:div.flex.items-center.justify-center.h-full.w-full.lg:w-1-4.lg:ml-2
       (cc/loading-spinner {:with-overlay false})]

      selected-property
      [map-info
       [:div.flex.items-center.mb-3
        {:class (if address "justify-between" "justify-end")}
        (when address
          [:h3.flex-1.font-bold.text-lg.pr-2.mb-0.ml-2 address])
        [:div.flex-shrink-0
         [ui/button {:icon     CloseFilled16
                     :kind     :ghost
                     :size     :small
                     :on-click #(>evt [::deselect-property])}
          (tr [:close])]]]
       [:<>
        [:div.flex-grow.overflow-y-auto.px-2
         [existing-feature-properties (util/mmap tr lang)]]
        [:div.flex.grid.justify-center.mb-2
         [ui/button {:size     :small
                     :on-click #(>evt [::begin-complaint])}
          (tr [:code-enforcement/create-complaint])]]]]

      (and (seq coordinates)
           (ff/enabled? :feature-flag.id/complaints-wo-property))
      (let [[x y]    coordinates
            location {:location.center/x    x
                      :location.center/y    y
                      :address              (<sub [::create-v2/manual-address])
                      :location/description (:location/description (<sub [::create-v2/manual-input-values]))}]
        [map-info
         [:div.flex.items-center.mb-3.justify-end
          [:div.flex-shrink-0
           [ui/button {:icon     CloseFilled16
                       :kind     :ghost
                       :size     :small
                       :on-click #(>evt [::deselect-property])}
            (tr [:close])]]]
         [:div.flex-grow.overflow-y-auto.px-2
          [create-v2/manual-location-entry tr (util/mmap x y)]]
         [:div.flex.grid.justify-center.my-2
          [ui/button {:size     :small
                      :disabled (str/blank? (:location/description (<sub [::create-v2/manual-input-values])))
                      :on-click #(>evt [::use-location location])}
           (tr [:code-enforcement/create-complaint])]]]))))

(defn- location-step [{:keys [tr lang]}]
  (let [selected-property     (<sub [::selected-property])
        municipalities        (<sub [::municipalities])
        group-id              (<sub [::group-id])
        map-center            (<sub [::map-center])
        coordinates           (<sub [::coordinates])
        point-search-loading? (<sub [::map-common-util/point-search-loading?])
        {:keys [geometry]}    selected-property]
    [:div
     [:p (tr [:public-map-view/instruction-how-to-complain-text])]
     [gis-search/address-search
      {:tr                 tr
       :domain-group-id    group-id
       :municipalities     (map :municipality/number municipalities)
       :municipality-names (->> (map #(hash-map (:municipality/number %) (:municipality/names %)) municipalities)
                                (apply merge))
       :disabled           (nil? group-id)
       :on-select-address  #(>evt [::select-property (:geometry-point %) %])
       :show-existing?     false
       :light              false}]
     (when map-center
       [:div.flex.items-center.flex-wrap.lg:flex-nowrap.mt-6
        [:div.flex.map-boundary.map-extended.bg-gray-400.rounded.overflow-hidden.h-full.w-full
         {:class (when (or point-search-loading?
                           selected-property
                           (and (seq coordinates)
                                (ff/enabled? :feature-flag.id/complaints-wo-property)))
                   ["lg:w-3-4" "lg:mr-2"])
          :style {:height "500px"}}
         [workspace-map/workspace-map
          {:center             map-center
           :layers             (<sub [::gis-map-layers])
           :extended?          true
           :primaryMapMarkers  (if (seq coordinates) [coordinates] [])
           :events             [{:eventType     "singleclick"
                                 :eventFunction #(>evt [::map-click
                                                        %
                                                        (p/assoc-if
                                                          {:group/id group-id}
                                                          :municipality/number (when (= (count municipalities) 1)
                                                                                 (-> municipalities
                                                                                     first
                                                                                     :municipality/number)))])}]
           :zoom               15
           :propertyGeometries (if geometry
                                 {:primaryPropertyGeometry (property-model/geojson-feature geometry)}
                                 {})}]]
        [property-details (util/mmap tr lang selected-property coordinates point-search-loading?)]])]))


(defn- form-step [{:keys [tr lang]}]
  (let [selected-property   (<sub [::selected-property])
        coordinates         (<sub [::coordinates])
        location            (<sub [::location])
        municipalities      (<sub [::municipalities])
        authority-groups    (<sub [::complaint-authority-groups])
        municipality-number (or (:municipality selected-property)
                                (-> municipalities first :municipality/number))]
    (when municipality-number
      [complaint-form/form (util/mmap {:authority-groups authority-groups
                                       :coordinates      coordinates
                                       :is-public?       true
                                       :on-cancel-click  #(>evt [::back-to-location])
                                       :command          :complaint/create-complaint-public}
                                      tr lang selected-property location municipality-number)])))


(defn- submitted-step [tr]
  [:div
   [:h2.text-2xl.font-bold.mb-4 (tr [:public-map-view/complaint-confirmation])]])


(defn- view [_match]
  (let [tr       (<sub [:tr])
        lang     (<sub [:language])
        loading? (<sub [::loading?])
        step     (<sub [::step])]
    (if loading?
      [:div.cloudpermit-page
       [section/section-skeleton]]
      [:div.cloudpermit-page
       [ui/page-header {}
        [:h1.bold (tr [:public-map-view/complaint-submission])]]
       [:section
        (case step
          :location  [location-step (util/mmap tr lang)]
          :form      [form-step (util/mmap tr lang)]
          :thank-you [submitted-step tr])]])))

(rf/reg-sub ::view-data
  :<- [::origin]
  :<- [:session/logged-in?]
  (fn [[origin logged-in?] _]
    {:back-href (href/href (previous-view origin logged-in?))}))

(def route
  {:name          :view/create-complaint
   :view          view
   :view-sub      [::view-data]
   :public-header landing-page/landing-header
   :public?       true
   :parameters    {:path  {:branding s/Str}
                   :query {(s/optional-key :origin) s/Str}}
   :controllers   [{:parameters {:path  [:branding]
                                 :query [:origin]}
                    :start      (fn [{{:keys [branding]} :path
                                      {:keys [origin]}   :query}]
                                  (>evt [::init branding origin])
                                  (>evt [::landing-page/init branding]))
                    :stop       (fn [_]
                                  (>evt [::leave]))}]})
